import { useEffect, useRef } from 'react'

interface UseSessionTimeoutProps {
  timeoutMinutes: number
  warningMinutes?: number
  onLogout: () => void
  isAuthenticated: boolean
}

export const useSessionTimeout = ({ 
  timeoutMinutes, 
  warningMinutes = 5,
  onLogout,
  isAuthenticated
}: UseSessionTimeoutProps) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const lastActivityRef = useRef<number>(Date.now())

  const resetTimeout = () => {
    lastActivityRef.current = Date.now()
    
    // Limpar timeouts existentes
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)

    // Configurar timeout de aviso
    const warningTime = (timeoutMinutes - warningMinutes) * 60 * 1000
    warningTimeoutRef.current = setTimeout(() => {
      showWarning()
    }, warningTime)

    // Configurar timeout de logout
    const logoutTime = timeoutMinutes * 60 * 1000
    timeoutRef.current = setTimeout(() => {
      handleLogout()
    }, logoutTime)
  }

  const showWarning = () => {
    const remainingMinutes = Math.ceil((timeoutRef.current ? 
      (timeoutRef.current as any)._idleStart + (timeoutRef.current as any)._idleTimeout - Date.now() 
      : 0) / 60000)

    if (remainingMinutes > 0) {
      const shouldExtend = window.confirm(
        `Sua sessão expirará em ${remainingMinutes} minutos. Deseja estender a sessão?`
      )
      
      if (shouldExtend) {
        resetTimeout()
      }
    }
  }

  const handleLogout = () => {
    alert('Sua sessão expirou. Você será redirecionado para a tela de login.')
    onLogout()
  }

  const handleActivity = () => {
    resetTimeout()
  }

  useEffect(() => {
    if (!isAuthenticated) {
      // Limpar timeouts se não estiver autenticado
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
      return
    }

    // Configurar listeners de atividade
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ]

    events.forEach(event => {
      document.addEventListener(event, handleActivity, true)
    })

    // Inicializar timeout
    resetTimeout()

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleActivity, true)
      })
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current)
    }
  }, [timeoutMinutes, warningMinutes, isAuthenticated])

  return {
    resetTimeout,
    getTimeRemaining: () => {
      const now = Date.now()
      const lastActivity = lastActivityRef.current
      const elapsed = now - lastActivity
      const remaining = (timeoutMinutes * 60 * 1000) - elapsed
      return Math.max(0, Math.ceil(remaining / 60000)) // retorna em minutos
    }
  }
}
